import React, { useEffect, useState } from "react";
import { useDealPipeline, Deal, DealDocument, DealStage, PIPELINE_STAGES } from "@/hooks/useDealPipeline";
import { DealPipelineView, DocumentSignatureCard, KanbanBoard, DealStatisticsCharts } from "@/components/deal-pipeline";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, 
  Search, 
  FileText, 
  Users, 
  TrendingUp, 
  Clock,
  CheckCircle2,
  AlertCircle,
  Building2,
  DollarSign,
  Calendar,
  RefreshCw,
  Download,
  Filter,
  X,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  List,
  BarChart3,
  ChevronUp
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format, isAfter, isBefore, parseISO } from "date-fns";
import { RequireStaff } from "@/components/RequireStaff";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";

const PIPELINE_T = {
  en: {
    title: "Deal Pipeline", subtitle: "Manage and track deals through the 11-step pipeline",
    refresh: "Refresh", newDeal: "New Deal", createNewDeal: "Create New Deal",
    addToPipeline: "Add a new deal to the pipeline", businessName: "Business Name *",
    enterBusinessName: "Enter business name", description: "Description",
    briefDescription: "Brief description of the business", askingPrice: "Asking Price",
    currency: "Currency", cancel: "Cancel", createDeal: "Create Deal",
    totalDeals: "Total Deals", pendingNDA: "Pending NDA", pendingLOI: "Pending LOI",
    ndaSigned: "NDA Signed", loiSigned: "LOI Signed", statistics: "Statistics",
    hideCharts: "Hide Charts", viewCharts: "View Charts",
    searchDeals: "Search deals...", allStages: "All Stages",
    advancedFilters: "Advanced Filters", exportCSV: "Export CSV",
    dateFrom: "Date From", dateTo: "Date To", minPrice: "Min Price", maxPrice: "Max Price",
    allCurrencies: "All Currencies", confidential: "Confidential", all: "All",
    confidentialOnly: "Confidential Only", nonConfidential: "Non-Confidential",
    clearFilters: "Clear Filters",
    deals: "Deals", name: "Name", price: "Price", date: "Date", stage: "Stage",
    noDeals: "No deals found", noDescription: "No description",
    pipeline: "Pipeline", documents: "Documents", details: "Details",
    pipelineProgress: "Pipeline Progress", dealDocuments: "Deal Documents",
    ndaLoiDocs: "NDA, LOI, and other documents", noDocuments: "No documents yet",
    docsAppear: "Documents will appear here when NDA/LOI are initiated",
    dealInfo: "Deal Information", currentStage: "Current Stage",
    confidentialLabel: "Confidential", yes: "Yes", no: "No",
    created: "Created", lastUpdated: "Last Updated",
    stageHistory: "Stage History", selectDeal: "Select a Deal",
    selectDealDesc: "Choose a deal from the list to view its pipeline progress, documents, and details.",
    search: "Search", from: "From", to: "To", min: "Min", max: "Max",
  },
  fr: {
    title: "Pipeline des Dossiers", subtitle: "Gérez et suivez les dossiers à travers les 11 étapes",
    refresh: "Actualiser", newDeal: "Nouveau Dossier", createNewDeal: "Créer un Nouveau Dossier",
    addToPipeline: "Ajouter un nouveau dossier au pipeline", businessName: "Nom de l'entreprise *",
    enterBusinessName: "Entrez le nom de l'entreprise", description: "Description",
    briefDescription: "Brève description de l'entreprise", askingPrice: "Prix demandé",
    currency: "Devise", cancel: "Annuler", createDeal: "Créer le Dossier",
    totalDeals: "Total Dossiers", pendingNDA: "NDA en Attente", pendingLOI: "LOI en Attente",
    ndaSigned: "NDA Signé", loiSigned: "LOI Signé", statistics: "Statistiques",
    hideCharts: "Masquer les Graphiques", viewCharts: "Voir les Graphiques",
    searchDeals: "Rechercher des dossiers...", allStages: "Toutes les Étapes",
    advancedFilters: "Filtres Avancés", exportCSV: "Exporter CSV",
    dateFrom: "Date Début", dateTo: "Date Fin", minPrice: "Prix Min", maxPrice: "Prix Max",
    allCurrencies: "Toutes les Devises", confidential: "Confidentiel", all: "Tous",
    confidentialOnly: "Confidentiel Seulement", nonConfidential: "Non-Confidentiel",
    clearFilters: "Effacer les Filtres",
    deals: "Dossiers", name: "Nom", price: "Prix", date: "Date", stage: "Étape",
    noDeals: "Aucun dossier trouvé", noDescription: "Aucune description",
    pipeline: "Pipeline", documents: "Documents", details: "Détails",
    pipelineProgress: "Progression du Pipeline", dealDocuments: "Documents du Dossier",
    ndaLoiDocs: "NDA, LOI et autres documents", noDocuments: "Aucun document",
    docsAppear: "Les documents apparaîtront ici lorsque les NDA/LOI seront initiés",
    dealInfo: "Informations du Dossier", currentStage: "Étape Actuelle",
    confidentialLabel: "Confidentiel", yes: "Oui", no: "Non",
    created: "Créé le", lastUpdated: "Dernière Mise à Jour",
    stageHistory: "Historique des Étapes", selectDeal: "Sélectionnez un Dossier",
    selectDealDesc: "Choisissez un dossier dans la liste pour voir sa progression, ses documents et ses détails.",
    search: "Recherche", from: "De", to: "À", min: "Min", max: "Max",
  },
};

export default function DealsPipeline() {
  const { language } = useLanguage();
  const pt = PIPELINE_T[language] || PIPELINE_T.en;
  const {
    loading, 
    fetchDeals, 
    fetchDealDocuments, 
    createDeal, 
    signDocument,
    moveDealToStage,
    PIPELINE_STAGES: stages 
  } = useDealPipeline();
  
  // View mode: list or kanban
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [showStatistics, setShowStatistics] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [dealDocuments, setDealDocuments] = useState<DealDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newDealForm, setNewDealForm] = useState({
    business_name: "",
    business_description: "",
    asking_price: "",
    currency: "CAD"
  });
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [confidentialFilter, setConfidentialFilter] = useState<"all" | "yes" | "no">("all");
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  
  // Sorting
  type SortField = "name" | "price" | "date" | "stage";
  type SortDirection = "asc" | "desc";
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    const data = await fetchDeals();
    setDeals(data);
  };

  const loadDealDocuments = async (dealId: string) => {
    const docs = await fetchDealDocuments(dealId);
    setDealDocuments(docs);
  };

  const handleSelectDeal = async (deal: Deal) => {
    setSelectedDeal(deal);
    await loadDealDocuments(deal.id);
  };

  const handleCreateDeal = async () => {
    const result = await createDeal({
      business_name: newDealForm.business_name,
      business_description: newDealForm.business_description,
      asking_price: newDealForm.asking_price ? parseFloat(newDealForm.asking_price) : undefined,
      currency: newDealForm.currency,
    });

    if (result) {
      setIsCreateDialogOpen(false);
      setNewDealForm({ business_name: "", business_description: "", asking_price: "", currency: "CAD" });
      await loadDeals();
    }
  };

  const handleSignDocument = async (documentId: string, signatureData: string, role: 'buyer' | 'seller' | 'broker') => {
    const success = await signDocument(documentId, signatureData, role);
    if (success && selectedDeal) {
      await loadDealDocuments(selectedDeal.id);
      await loadDeals();
    }
  };

  const handleDealMove = async (dealId: string, newStage: DealStage) => {
    const success = await moveDealToStage(dealId, newStage);
    if (success) {
      await loadDeals();
    }
  };

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         deal.business_description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === "all" || deal.current_stage === stageFilter;
    
    // Date filters
    const dealDate = parseISO(deal.created_at);
    const matchesDateFrom = !dateFrom || isAfter(dealDate, parseISO(dateFrom));
    const matchesDateTo = !dateTo || isBefore(dealDate, parseISO(dateTo + "T23:59:59"));
    
    // Price filters
    const matchesPriceMin = !priceMin || (deal.asking_price && deal.asking_price >= parseFloat(priceMin));
    const matchesPriceMax = !priceMax || (deal.asking_price && deal.asking_price <= parseFloat(priceMax));
    
    // Confidential filter
    const matchesConfidential = confidentialFilter === "all" || 
      (confidentialFilter === "yes" && deal.is_confidential) ||
      (confidentialFilter === "no" && !deal.is_confidential);
    
    // Currency filter
    const matchesCurrency = currencyFilter === "all" || deal.currency === currencyFilter;
    
    return matchesSearch && matchesStage && matchesDateFrom && matchesDateTo && 
           matchesPriceMin && matchesPriceMax && matchesConfidential && matchesCurrency;
  });

  // Sorting logic
  const getStageOrder = (stage: string): number => {
    return stages.findIndex(s => s.stage === stage);
  };

  const sortedDeals = [...filteredDeals].sort((a, b) => {
    const multiplier = sortDirection === "asc" ? 1 : -1;
    
    switch (sortField) {
      case "name":
        return multiplier * a.business_name.localeCompare(b.business_name);
      case "price":
        const priceA = a.asking_price || 0;
        const priceB = b.asking_price || 0;
        return multiplier * (priceA - priceB);
      case "date":
        return multiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case "stage":
        return multiplier * (getStageOrder(a.current_stage) - getStageOrder(b.current_stage));
      default:
        return 0;
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3" /> 
      : <ArrowDown className="h-3 w-3" />;
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStageFilter("all");
    setDateFrom("");
    setDateTo("");
    setPriceMin("");
    setPriceMax("");
    setConfidentialFilter("all");
    setCurrencyFilter("all");
  };

  const hasActiveFilters = searchQuery || stageFilter !== "all" || dateFrom || dateTo || 
                           priceMin || priceMax || confidentialFilter !== "all" || currencyFilter !== "all";

  const exportToCSV = () => {
    const headers = [
      "Business Name",
      "Description",
      "Asking Price",
      "Currency",
      "Stage",
      "Confidential",
      "Created At",
      "Updated At"
    ];

    const csvRows = [
      headers.join(","),
      ...filteredDeals.map(deal => [
        `"${deal.business_name.replace(/"/g, '""')}"`,
        `"${(deal.business_description || "").replace(/"/g, '""')}"`,
        deal.asking_price || "",
        deal.currency,
        stages.find(s => s.stage === deal.current_stage)?.label || deal.current_stage,
        deal.is_confidential ? "Yes" : "No",
        format(new Date(deal.created_at), "yyyy-MM-dd HH:mm"),
        format(new Date(deal.updated_at), "yyyy-MM-dd HH:mm")
      ].join(","))
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `deals-export-${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStageStats = () => {
    const stats: Record<string, number> = {};
    stages.forEach(s => { stats[s.stage] = 0; });
    deals.forEach(d => { stats[d.current_stage] = (stats[d.current_stage] || 0) + 1; });
    return stats;
  };

  const stageStats = getStageStats();

  const getStageBadgeVariant = (stage: string): "default" | "secondary" | "destructive" | "outline" => {
    if (stage.includes('signed') || stage === 'closing') return "default";
    if (stage.includes('pending')) return "secondary";
    return "outline";
  };

  const formatCurrency = (amount: number | undefined, currency: string) => {
    if (!amount) return "—";
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
  };

  return (
    <RequireStaff>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{pt.title}</h1>
            <p className="text-muted-foreground">{pt.subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-r-none"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "kanban" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("kanban")}
                className="rounded-l-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={loadDeals} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {pt.refresh}
            </Button>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {pt.newDeal}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{pt.createNewDeal}</DialogTitle>
                  <DialogDescription>{pt.addToPipeline}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">{pt.businessName}</Label>
                    <Input
                      id="business_name"
                      value={newDealForm.business_name}
                      onChange={(e) => setNewDealForm(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder={pt.enterBusinessName}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_description">{pt.description}</Label>
                    <Textarea
                      id="business_description"
                      value={newDealForm.business_description}
                      onChange={(e) => setNewDealForm(prev => ({ ...prev, business_description: e.target.value }))}
                      placeholder={pt.briefDescription}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="asking_price">{pt.askingPrice}</Label>
                      <Input
                        id="asking_price"
                        type="number"
                        value={newDealForm.asking_price}
                        onChange={(e) => setNewDealForm(prev => ({ ...prev, asking_price: e.target.value }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency">{pt.currency}</Label>
                      <select
                        id="currency"
                        className="w-full px-3 py-2 border rounded-md bg-background"
                        value={newDealForm.currency}
                        onChange={(e) => setNewDealForm(prev => ({ ...prev, currency: e.target.value }))}
                      >
                        <option value="CAD">CAD</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>{pt.cancel}</Button>
                  <Button onClick={handleCreateDeal} disabled={!newDealForm.business_name || loading}>
                    {pt.createDeal}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{pt.totalDeals}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{deals.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">{pt.pendingNDA}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stageStats['lender_submission'] || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">{pt.pendingNDA}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stageStats['lender_submission'] || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">{pt.pendingLOI}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stageStats['buyer_contact'] || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">{pt.loiSigned}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stageStats['loi_signed'] || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 cursor-pointer hover:bg-card/80 transition-colors" onClick={() => setShowStatistics(!showStatistics)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">{pt.statistics}</span>
                  </div>
                  <p className="text-sm font-medium mt-1 text-primary">
                    {showStatistics ? pt.hideCharts : pt.viewCharts}
                  </p>
                </div>
                <ChevronUp className={`h-4 w-4 text-muted-foreground transition-transform ${showStatistics ? '' : 'rotate-180'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Statistics Charts Section */}
        <Collapsible open={showStatistics} onOpenChange={setShowStatistics}>
          <CollapsibleContent className="animate-accordion-down">
            <DealStatisticsCharts deals={deals} />
          </CollapsibleContent>
        </Collapsible>

        {/* Filters */}
        <Card className="bg-card/50">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={pt.searchDeals}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                className="px-3 py-2 border rounded-md bg-background min-w-[180px]"
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <option value="all">{pt.allStages}</option>
                {stages.map(s => (
                  <option key={s.stage} value={s.stage}>{s.label}</option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                {pt.advancedFilters}
                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                disabled={filteredDeals.length === 0}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {pt.exportCSV}
              </Button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.dateFrom}</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.dateTo}</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.minPrice}</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.maxPrice}</Label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.currency}</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    value={currencyFilter}
                    onChange={(e) => setCurrencyFilter(e.target.value)}
                  >
                    <option value="all">{pt.allCurrencies}</option>
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{pt.confidential}</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    value={confidentialFilter}
                    onChange={(e) => setConfidentialFilter(e.target.value as "all" | "yes" | "no")}
                  >
                    <option value="all">{pt.all}</option>
                    <option value="yes">{pt.confidentialOnly}</option>
                    <option value="no">{pt.nonConfidential}</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    {pt.clearFilters}
                  </Button>
                </div>
              </div>
            )}

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 pt-2">
                {searchQuery && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.search}: "{searchQuery}"
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSearchQuery("")} />
                  </Badge>
                )}
                {stageFilter !== "all" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.stage}: {stages.find(s => s.stage === stageFilter)?.label}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setStageFilter("all")} />
                  </Badge>
                )}
                {dateFrom && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.from}: {dateFrom}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setDateFrom("")} />
                  </Badge>
                )}
                {dateTo && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.to}: {dateTo}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setDateTo("")} />
                  </Badge>
                )}
                {priceMin && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.min}: ${priceMin}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setPriceMin("")} />
                  </Badge>
                )}
                {priceMax && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.max}: ${priceMax}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setPriceMax("")} />
                  </Badge>
                )}
                {currencyFilter !== "all" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {pt.currency}: {currencyFilter}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setCurrencyFilter("all")} />
                  </Badge>
                )}
                {confidentialFilter !== "all" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {confidentialFilter === "yes" ? pt.confidentialOnly : pt.nonConfidential}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setConfidentialFilter("all")} />
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content */}
        {viewMode === "kanban" ? (
          /* Kanban View */
          <KanbanBoard
            deals={sortedDeals}
            onDealMove={handleDealMove}
            onDealSelect={handleSelectDeal}
            selectedDealId={selectedDeal?.id}
          />
        ) : (
          /* List View */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Deals List */}
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{pt.deals} ({sortedDeals.length})</h2>
            </div>
            
            {/* Sort Buttons */}
            <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
              <Button
                variant={sortField === "name" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleSort("name")}
                className="flex items-center gap-1 text-xs"
              >
                {pt.name} <SortIcon field="name" />
              </Button>
              <Button
                variant={sortField === "price" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleSort("price")}
                className="flex items-center gap-1 text-xs"
              >
                {pt.price} <SortIcon field="price" />
              </Button>
              <Button
                variant={sortField === "date" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleSort("date")}
                className="flex items-center gap-1 text-xs"
              >
                {pt.date} <SortIcon field="date" />
              </Button>
              <Button
                variant={sortField === "stage" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => handleSort("stage")}
                className="flex items-center gap-1 text-xs"
              >
                {pt.stage} <SortIcon field="stage" />
              </Button>
            </div>

            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
              {sortedDeals.length === 0 ? (
                <Card className="bg-card/50">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>{pt.noDeals}</p>
                  </CardContent>
                </Card>
              ) : (
                sortedDeals.map(deal => (
                  <Card
                    key={deal.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedDeal?.id === deal.id ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => handleSelectDeal(deal)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium truncate">{deal.business_name}</h3>
                          <p className="text-sm text-muted-foreground truncate">
                            {deal.business_description || pt.noDescription}
                          </p>
                        </div>
                        <Badge variant={getStageBadgeVariant(deal.current_stage)}>
                          {stages.find(s => s.stage === deal.current_stage)?.label || deal.current_stage}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {formatCurrency(deal.asking_price, deal.currency)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(deal.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Deal Details */}
          <div className="lg:col-span-2">
            {selectedDeal ? (
              <Tabs defaultValue="pipeline" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="pipeline">{pt.pipeline}</TabsTrigger>
                  <TabsTrigger value="documents">{pt.documents} ({dealDocuments.length})</TabsTrigger>
                  <TabsTrigger value="details">{pt.details}</TabsTrigger>
                </TabsList>

                <TabsContent value="pipeline" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>{selectedDeal.business_name}</CardTitle>
                      <CardDescription>{pt.pipelineProgress}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DealPipelineView
                        currentStage={selectedDeal.current_stage}
                        stageHistory={selectedDeal.stage_history}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="documents" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>{pt.dealDocuments}</CardTitle>
                      <CardDescription>{pt.ndaLoiDocs}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {dealDocuments.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>{pt.noDocuments}</p>
                          <p className="text-sm">{pt.docsAppear}</p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          {dealDocuments.map(doc => (
                            <DocumentSignatureCard
                              key={doc.id}
                              document={doc}
                              onSigned={() => selectedDeal && loadDealDocuments(selectedDeal.id)}
                            />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>{pt.dealInfo}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">{pt.businessName}</Label>
                          <p className="font-medium">{selectedDeal.business_name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">{pt.askingPrice}</Label>
                          <p className="font-medium">{formatCurrency(selectedDeal.asking_price, selectedDeal.currency)}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">{pt.currentStage}</Label>
                          <p className="font-medium">{stages.find(s => s.stage === selectedDeal.current_stage)?.label}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">{pt.confidentialLabel}</Label>
                          <p className="font-medium">{selectedDeal.is_confidential ? pt.yes : pt.no}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">{pt.created}</Label>
                          <p className="font-medium">{format(new Date(selectedDeal.created_at), 'PPP')}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">{pt.lastUpdated}</Label>
                          <p className="font-medium">{format(new Date(selectedDeal.updated_at), 'PPP')}</p>
                        </div>
                      </div>
                      {selectedDeal.business_description && (
                        <div>
                          <Label className="text-muted-foreground">{pt.description}</Label>
                          <p className="mt-1">{selectedDeal.business_description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{pt.stageHistory}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedDeal.stage_history.map((entry, idx) => (
                          <div key={idx} className="flex items-start gap-3 pb-3 border-b last:border-0">
                            <div className="h-2 w-2 mt-2 rounded-full bg-primary" />
                            <div className="flex-1">
                              <p className="font-medium">
                                {stages.find(s => s.stage === entry.stage)?.label || entry.stage}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(entry.timestamp), 'PPP p')}
                              </p>
                              {entry.notes && (
                                <p className="text-sm mt-1">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            ) : (
              <Card className="h-full flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium">{pt.selectDeal}</p>
                  <p className="text-sm">{pt.selectDealDesc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        )}
      </div>
    </RequireStaff>
  );
}
